import { Injectable } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BehaviorSubject, Subject } from 'rxjs';
import { CurriculumConstants } from '../constants/curriculum.contants';
import { Curriculum } from '../models/curriculum';
import { CurriculumDetails } from '../models/curriculumDetails';
import { CurriculumService } from './curriculum.service';
import { CourseCatalogueSearchResult } from '../../course-catalogue/models/course-catalogue-search-result';
import { catchError, throwError } from 'rxjs';
import { AssignmentGroupInteractionService } from '../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { CurriculumTrainingItems } from '../models/curriculumTrainingItems';
import { TrainingItemGroupDetails } from '../../training-item/models/training-item-group-detail';
import { TrainingItemConstants } from '../../training-item/constants/training-item-constants';

@Injectable({
  providedIn: 'root'
})

export class CurriculumPersistenceService {
  closeAndResetCurriculumSlider$ = new Subject<boolean>();
  curriculumSaveInProgress$ = new BehaviorSubject<boolean>(false);
  currentTabInSliderMenu$ = new Subject<number>();
  currentTabInSliderMenuByCustomChange$ = new Subject<number>();
  populateErrorsForTab$ = new BehaviorSubject<boolean>(false);
  errorTabIndices: number[] = [];
  curriculum$ = new BehaviorSubject<Curriculum | null>(null);
  trainingItemsList: CurriculumTrainingItems[] = []
  curriculumDetails !: CurriculumDetails;
  duplicateTrainingItemsNumber: string[] = [];
  duplicateTrainingItemNumbersWithRemoveStatus: string[] = [];

  constructor(private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private curriculumService: CurriculumService,
    private assignmentGroupInteractionService: AssignmentGroupInteractionService
  ) { }

  openConfirmationDialog = () => {
    this.confirmationService.confirm({
      message: CurriculumConstants.Curriculum_Slider_Confirmation_Text,
      header: CurriculumConstants.Curriculum_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetCurriculumSlider$.next(true);
      },
    });
  }

  onTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenu$.next(tabIndex);
  }
  onCustomTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenuByCustomChange$.next(tabIndex);
  }

  updateTabErrorIndex = (tabIndex: number, isError: boolean) => {
    isError ? this.addErrorTabIndex(tabIndex) : this.removeErrorTabIndex(tabIndex);
    this.populateErrorsForTab$.next(true);
  }

  // To Add Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  addErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) === -1) {
      this.errorTabIndices.push(tabIndex);
    }
  }

  // To Remove Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  removeErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) > -1) {
      this.errorTabIndices.splice(this.errorTabIndices.indexOf(tabIndex), 1);
    }
  }

  updateCurriculumDetails = (curriculum: Curriculum) => {
    this.curriculum$.next(curriculum);
  }

  prepareDataForDocumentTrainingItem(documents: any[], isUpdate: boolean, scope?: number) {
    this.trainingItemsList = [];
    if (isUpdate) {
      this.curriculum$.value?.trainingItems.forEach(training => {
        const trainingItem: CurriculumTrainingItems = {
          trainingItemId: training.trainingItemId,
          id: training.id,
          type: training.type,
          status: training.status,
          coreOrElective: training.coreOrElective,
          trainingItemGroups: training.trainingItemGroups,
          unAssignedTrainingItemGroups: training.unAssignedTrainingItemGroups
        }
        this.trainingItemsList.push(trainingItem);
      })
    }
    documents.forEach(doc => {
      const trainingItem: CurriculumTrainingItems = this.createCurriculumTrainingItem(doc.number, CurriculumConstants.Curriculum_DocumentType, null);
      this.trainingItemsList.push(trainingItem);
    })
    var curriculum: CurriculumDetails = {
      parentEqmsScope: scope,
      trainingItems: this.trainingItemsList
    }
    this.curriculumDetails = curriculum;
  }

  addCurriculum(addAndClose: boolean) {
    this.curriculumService.addCurriculum(this.curriculumDetails)
      .pipe(
        catchError((err) => {
          this.messageService.add({
            severity: CurriculumConstants.Severity_Err,
            summary: `${err.error}`,
          });
          return throwError(() => err);
        })
      ).subscribe((serviceData) => {
        this.messageService.add({
          severity: CurriculumConstants.Severity_Success,
          summary: CurriculumConstants.Curriculum_CreatedMessage
        })
        if (addAndClose) {
          this.closeAndResetCurriculumSlider$.next(true);
        }
        this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
      });
  }

  updateCurriculum(addAndClose: boolean) {
    this.curriculumService.updateCurriculum(this.curriculumDetails)
      .pipe(
        catchError((err) => {
          this.messageService.add({
            severity: CurriculumConstants.Severity_Err,
            summary: `${err.error}`,
          });
          return throwError(() => err);
        })
      ).subscribe((serviceData) => {
        this.messageService.add({
          severity: CurriculumConstants.Severity_Success,
          summary: CurriculumConstants.Curriculum_UpdatedMessage
        })
        if (addAndClose) {
          this.closeAndResetCurriculumSlider$.next(true);
        }
        this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
      });
  }

  prepareDataForCourseTrainingItem(courses: CourseCatalogueSearchResult[], isUpdate: boolean, scope?: number) {
    this.trainingItemsList = []
    if (isUpdate) {
      this.curriculum$.value?.trainingItems.forEach(training => {
        const trainingItem: CurriculumTrainingItems = {
          trainingItemId: training.trainingItemId,
          id: training.id,
          type: training.type,
          status: training.status,
          coreOrElective: training.coreOrElective,
          trainingItemGroups: training.trainingItemGroups,
          unAssignedTrainingItemGroups: training.unAssignedTrainingItemGroups
        }
        this.trainingItemsList.push(trainingItem);
      })
    }
    courses.forEach(course => {
      const trainingItem: CurriculumTrainingItems = this.createCurriculumTrainingItem(course.courseNumber, CurriculumConstants.Curriculum_CourseType, null);
      this.trainingItemsList.push(trainingItem);
    })
    var curriculum: CurriculumDetails = {
      parentEqmsScope: scope,
      trainingItems: this.trainingItemsList
    }
    this.curriculumDetails = curriculum;
  }

  prepareDataForTrainingItemGroupTrainingItem(trainingItemGroups: TrainingItemGroupDetails[], isUpdate: boolean, scope?: number) {
    this.trainingItemsList = [];
    this.duplicateTrainingItemsNumber = [];
    this.duplicateTrainingItemNumbersWithRemoveStatus = [];
    if (isUpdate) {
      this.populateTrainingItemsFromCurriculum();
    }
    trainingItemGroups.forEach((trainingItemGroup) => {
      trainingItemGroup.trainingItems.forEach((trainingItem) => {
        if (this.trainingItemsList.length > 0) {
          const existingItem = this.trainingItemsList
            .find(item => item.id === trainingItem.trainingItemNumber);
          
          // This is to check if the Training Item is Scheduled in TIG and existing TI in Curriculum is Active, to avoid changing status to scheduled and adding TIG reference
          const isTIGTrainingItemScheduledWhileExistingIsActive = trainingItem.scheduled && existingItem?.status == CurriculumConstants.Curriculum_StatusActive;

          if (existingItem) {
            if (existingItem.status === CurriculumConstants.Curriculum_StatusRemoved) {
              this.duplicateTrainingItemNumbersWithRemoveStatus.push(trainingItem.trainingItemNumber);
            } else {
              this.duplicateTrainingItemsNumber.push(trainingItem.trainingItemNumber);
            }

            // If Training Item Groups is null then initialize with current TrainingItemGroup

            if (!Array.isArray(existingItem.trainingItemGroups) && !isTIGTrainingItemScheduledWhileExistingIsActive) {
                existingItem.trainingItemGroups = [trainingItemGroup.number];
                existingItem.unAssignedTrainingItemGroups = null
                return;
            }

            // To Removed all unassigned TIGs before adding new or re-adding 
            // unassigned TIG to the training item from UI
            if (Array.isArray(existingItem.unAssignedTrainingItemGroups)
              && existingItem.unAssignedTrainingItemGroups.length > 0) {
              existingItem.unAssignedTrainingItemGroups.forEach(unassignedTrainingItemGroup => {
                const indexOfUnAssignedTrainingItemGroup = existingItem.trainingItemGroups?.indexOf(unassignedTrainingItemGroup);
                if (indexOfUnAssignedTrainingItemGroup !== undefined &&
                  indexOfUnAssignedTrainingItemGroup > -1) {
                  existingItem.trainingItemGroups?.splice(indexOfUnAssignedTrainingItemGroup, 1);
                }
              });
            }
            existingItem.unAssignedTrainingItemGroups = null;

            // Adding the Training Item Group to the Training Item and making it active
            const hasGroups = Array.isArray(existingItem.trainingItemGroups) && existingItem.trainingItemGroups.length > 0;
            const isTrainingItemGroupExists = hasGroups && existingItem.trainingItemGroups!
              .includes(trainingItemGroup.number);
            if(!isTIGTrainingItemScheduledWhileExistingIsActive && !isTrainingItemGroupExists) {
              if (hasGroups) {
                existingItem.trainingItemGroups!.push(trainingItemGroup.number);
              } else {
                existingItem.trainingItemGroups = [trainingItemGroup.number];
              }
            }
            
            if (existingItem.status === CurriculumConstants.Curriculum_StatusRemoved) {
              trainingItem.scheduled ? 
                existingItem.status = CurriculumConstants.Curriculum_StatusScheduled : 
                existingItem.status = CurriculumConstants.Curriculum_StatusActive;

              existingItem.coreOrElective = CurriculumConstants.Curriculum_Core;
            } else if ( existingItem.status === CurriculumConstants.Curriculum_StatusScheduled && !trainingItem.scheduled ) {
              existingItem.status = CurriculumConstants.Curriculum_StatusActive;
            }
          } else {
            const trainingItemStatus = trainingItem.scheduled ? CurriculumConstants.Curriculum_StatusScheduled : CurriculumConstants.Curriculum_StatusActive;
            this.addTrainingItem(trainingItem.trainingItemNumber, trainingItem.trainingItemType, trainingItemGroup.number, trainingItemStatus);
          }
        } else {
          const trainingItemStatus = trainingItem.scheduled ? CurriculumConstants.Curriculum_StatusScheduled : CurriculumConstants.Curriculum_StatusActive;
          this.addTrainingItem(trainingItem.trainingItemNumber, trainingItem.trainingItemType, trainingItemGroup.number, trainingItemStatus);
        };
      })
    });
    var curriculum: CurriculumDetails = {
      parentEqmsScope: scope,
      trainingItems: this.trainingItemsList
    }
    this.curriculumDetails = curriculum;
  }


  private createCurriculumTrainingItem(id: string, type: number, trainingItemGroupNumber: string | null, status : number = CurriculumConstants.Curriculum_StatusActive): CurriculumTrainingItems {
    let curriculumTrainingItem: CurriculumTrainingItems = {
      trainingItemId: undefined,
      id: id,
      type: type,
      status: status,
      coreOrElective: CurriculumConstants.Curriculum_Core,
      trainingItemGroups: trainingItemGroupNumber !== null ? [trainingItemGroupNumber] : null,
      unAssignedTrainingItemGroups: null
    };
    return curriculumTrainingItem;
  }

  private populateTrainingItemsFromCurriculum = () => {
    this.curriculum$.value?.trainingItems.forEach(training => {
      const item: CurriculumTrainingItems = {
        trainingItemId: training.trainingItemId,
        id: training.id,
        type: training.type,
        status: training.status,
        coreOrElective: training.coreOrElective,
        trainingItemGroups: training.trainingItemGroups,
        unAssignedTrainingItemGroups: training.unAssignedTrainingItemGroups
      };
      this.trainingItemsList.push(item);
    });
  }

  private addTrainingItem(trainingItemNumber: string, type: number, trainingItemGroupNumber: string | null, status: number = CurriculumConstants.Curriculum_StatusActive): void {
    if (type === TrainingItemConstants.Training_Item_Type_Document) {
      type = CurriculumConstants.Curriculum_DocumentType;
    } else if (type === TrainingItemConstants.Training_Item_Type_Course) {
      type = CurriculumConstants.Curriculum_CourseType;
    } else {
      return;
    }
    const trainingItem = this.createCurriculumTrainingItem(trainingItemNumber, type, trainingItemGroupNumber, status);
    this.trainingItemsList.push(trainingItem);
  }
}
